import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api, apiPublic } from './api.js';
import { emitError } from './toastBus.js';
import {
  useOutbox, overlayRun, overlayItem, enqueueItem, enqueueMark, enqueuePhoto, baseOf,
} from './offline/outbox.js';
import type {
  Org, Team, TeamMember, OrgMember, MemberLeave,
  Equipment, Category,
  Asset, AssetSection,
  Schedule, RosterWeek,
  CheckRun, CheckItem, CheckPhoto, PresignedUpload,
  CheckStatus, RunStatus, UserRole, AssetType, EquipType, EquipKind,
  Invite, InvitePreview, ClaimResult,
  AuditEntry, AuditGroup,
  Location, LocationEquipment,
  TeamRole, MemberLevel, UnitCatalogue, QualificationType, PositionType, MyUnitProfile,
  DutyRoster, Holiday, Shift, MyShift, CoverRequest, CoverOptions, CoverKind,
  SwapOffer, SwapOptions, SwapSide, OpsWeek, OpsMember, OpsPreference,
  Activity, ActivityDetail, ActivityKind, ActivityResponse, TrainingNight, MyCoverStats,
  ScratchWindow, ScratchWindowDetail,
  Fault, FaultDetail, FaultList, FaultStatus, OffRunAsset,
  QuietHours, QuietHoursState, ShiftReminderHours, CalendarFeed, UnitReport,
} from '@keetcheck/shared';

// ── Extended types returned by detail endpoints ────────────────

export interface CheckItemDetail extends CheckItem {
  sectionName?: string | null;
  equipmentName?: string;
  equipmentType?: EquipType;
  equipmentKind?: EquipKind;
  serialNumber?: string | null;
  photos: CheckPhoto[];
}

export interface CheckRunDetail extends CheckRun {
  assetName: string;
  assetType?: AssetType;
  teamName: string;
  items: CheckItemInRun[];
  itemCount: number;
  checkedCount: number;
  issueCount: number;
}

export interface CheckItemInRun extends CheckItem {
  sectionName?: string | null;
  sectionSortOrder?: number | null;
  position?: number | null;
  equipmentName?: string;
  equipmentType?: EquipType;
  equipmentKind?: EquipKind;
  serialNumber?: string | null;
  photoCount: number;
}

export interface CheckRunSummary extends CheckRun {
  assetName: string;
  assetType?: AssetType;
  teamName: string;
  itemCount: number;
  checkedCount: number;
  issueCount: number;
}

export interface SearchResult {
  id: string;
  name: string;
  equipmentType: string;
  serialNumber: string | null;
  categoryName: string | null;
  expiryDate: string | null;
  serviceDueDate: string | null;
  assetName: string | null;
  locationName: string | null;
}

// ── Org ──────────────────────────────────────────────────────

export function useOrg(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId],
    queryFn: () => api.get<Org>(`/orgs/${orgId}`),
    enabled: !!orgId,
  });
}

// ── Own profile ──────────────────────────────────────────────

/** A unit the person belongs to, with their role there. */
export interface MyOrg {
  id: string;
  name: string;
  role: UserRole;
}

export interface Me {
  id: string;
  email: string;
  displayName: string;
  /** Role in the unit in use (orgId). */
  role: UserRole;
  orgId: string;
  /** Every unit they can switch to. */
  orgs: MyOrg[];
  /** Also a system admin (runs the service across units). */
  systemAdmin: boolean;
  mfaSetupRequired: boolean;  // role needs MFA and none is enrolled yet
  team: { id: string; name: string; role: TeamRole } | null;
  unit: MyUnitProfile | null;
  /** Email me notifications when no device has push on. */
  emailNotifications: boolean;
  /** Hours before a shift to remind me; 0 = off. */
  shiftReminderHours: ShiftReminderHours;
  /** Admins: last month's unit report by email on the 1st. */
  monthlyReportEmail: boolean;
  /** Set when email to my address bounced or was marked as spam. */
  emailSuppressed: 'bounce' | 'complaint' | null;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/me'),
  });
}

export function useUpdateMyName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => api.patch<{ displayName: string }>('/me', { displayName }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useSetEmailNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (emailNotifications: boolean) => api.patch<{ emailNotifications: boolean }>('/me', { emailNotifications }),
    onSuccess: (_d, emailNotifications) => qc.setQueryData<Me>(['me'], (me) => me && { ...me, emailNotifications }),
  });
}

export function useSetShiftReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shiftReminderHours: ShiftReminderHours) => api.patch<{ shiftReminderHours: ShiftReminderHours }>('/me', { shiftReminderHours }),
    onSuccess: (_d, shiftReminderHours) => qc.setQueryData<Me>(['me'], (me) => me && { ...me, shiftReminderHours }),
  });
}

export function useSetMonthlyReportEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (monthlyReportEmail: boolean) => api.patch<{ monthlyReportEmail: boolean }>('/me', { monthlyReportEmail }),
    onSuccess: (_d, monthlyReportEmail) => qc.setQueryData<Me>(['me'], (me) => me && { ...me, monthlyReportEmail }),
  });
}

/** Admin → Reports: how the unit did over a period (local dates, inclusive). */
export function useUnitReport(orgId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'report', from, to],
    queryFn: () => api.get<UnitReport>(`/orgs/${orgId}/reports/summary?from=${from}&to=${to}`),
    enabled: !!orgId && !!from && !!to && from <= to,
    staleTime: 5 * 60_000,
  });
}

export function useEmailReport(orgId: string) {
  return useMutation({
    mutationFn: (p: { from: string; to: string }) => api.post<{ sentTo: string }>(`/orgs/${orgId}/reports/email`, p),
  });
}

/** My calendar feed's address (or a member's, for a leader/admin), fetched when asked for. */
export function useCalendarFeed(enabled: boolean, orgId?: string, userId?: string) {
  return useQuery({
    queryKey: ['calendar-feed', userId ?? 'me'],
    queryFn: () => api.get<CalendarFeed>(userId ? `/orgs/${orgId}/members/${userId}/calendar` : '/me/calendar'),
    enabled,
    staleTime: Infinity,
  });
}

export function useResetCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CalendarFeed>('/me/calendar/reset', {}),
    onSuccess: (feed) => qc.setQueryData(['calendar-feed', 'me'], feed),
  });
}

/** My quiet hours (when notifications wait), and whether I'm paused now. */
export function useQuietHours() {
  return useQuery({
    queryKey: ['me', 'quiet-hours'],
    queryFn: () => api.get<QuietHoursState>('/me/push/quiet'),
  });
}

export function useSetQuietHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (settings: QuietHours) => api.put<QuietHoursState>('/me/push/quiet', settings),
    onSuccess: (state) => qc.setQueryData(['me', 'quiet-hours'], state),
  });
}

// ── Audit log ────────────────────────────────────────────────

export function useAuditLog(orgId: string, group?: AuditGroup) {
  return useInfiniteQuery({
    queryKey: ['orgs', orgId, 'audit', group ?? 'all'],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (group) qs.set('group', group);
      if (pageParam) qs.set('before', pageParam);
      return api.get<AuditEntry[]>(`/orgs/${orgId}/audit?${qs}`);
    },
    initialPageParam: '',
    // Server pages are 50; a short page means we've reached the start.
    getNextPageParam: (last) => (last.length === 50 ? last[last.length - 1]!.id : undefined),
    enabled: !!orgId,
  });
}

// ── Org members ──────────────────────────────────────────────

export function useOrgMembers(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'members'],
    queryFn: () => api.get<OrgMember[]>(`/orgs/${orgId}/members`),
    enabled: !!orgId,
  });
}

export function useUpdateMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UserRole }) =>
      api.patch<OrgMember>(`/orgs/${orgId}/members/${userId}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
  });
}

/** Admin renames a member (their name everywhere, including history). */
export function useRenameMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, displayName }: { userId: string; displayName: string }) =>
      api.patch<{ userId: string; displayName: string }>(`/orgs/${orgId}/members/${userId}`, { displayName }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId] }), // members, teams, runs, dashboard all show names
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]),
  });
}

/** Admin edits a member's unit details: operational, level, UDO. */
export function useUpdateMemberUnit(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: { userId: string; operational?: boolean; level?: MemberLevel | null; udo?: boolean }) =>
      api.patch<{ userId: string }>(`/orgs/${orgId}/members/${userId}`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]),
  });
}

export function useSetMemberQualifications(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, qualifications }: {
      userId: string;
      qualifications: { typeId: string; grade?: string | null; expiresAt?: string | null }[];
    }) => api.put(`/orgs/${orgId}/members/${userId}/qualifications`, { qualifications }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]),
  });
}

export function useSetMemberPositions(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, positionIds }: { userId: string; positionIds: string[] }) =>
      api.put(`/orgs/${orgId}/members/${userId}/positions`, { positionIds }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]),
  });
}

// ── Unit catalogue (qualification and position types) ───────

export function useUnitCatalogue(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'unit-catalogue'],
    queryFn: () => api.get<UnitCatalogue>(`/orgs/${orgId}/unit-catalogue`),
    enabled: !!orgId,
  });
}

type QualTypeInput = { code?: string; name?: string; hasExpiry?: boolean; grades?: string[] | null; active?: boolean };

export function useSaveQualificationType(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: QualTypeInput & { id?: string }) => id
      ? api.patch<QualificationType>(`/orgs/${orgId}/qualification-types/${id}`, body)
      : api.post<QualificationType>(`/orgs/${orgId}/qualification-types`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'unit-catalogue'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]),
  });
}

export function useSavePositionType(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; name?: string; active?: boolean }) => id
      ? api.patch<PositionType>(`/orgs/${orgId}/position-types/${id}`, body)
      : api.post<PositionType>(`/orgs/${orgId}/position-types`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'unit-catalogue'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ]),
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/orgs/${orgId}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
  });
}

export interface NewMember {
  displayName: string; email?: string | null; teamId?: string | null;
  operational?: boolean; level?: MemberLevel | null; udo?: boolean;
}

/** Admin adds members without an app login (one, or a pasted list). */
export function useCreateMembers(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (members: NewMember[]) =>
      api.post<{ created: { userId: string; displayName: string }[]; skipped: string[] }>(`/orgs/${orgId}/members`, { members }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'teams'] }),
    ]),
  });
}

// ── Leave (admins, and the member's team leader/deputy) ─────────

export type LeaveEntry = MemberLeave & { past: boolean };
export type LeaveSaved = MemberLeave & { conflicts: { periodStart: string; kind: string; shifts: number }[]; releasedOps: number };

export function useMemberLeave(orgId: string, userId: string | null) {
  return useQuery({
    queryKey: ['orgs', orgId, 'leave', userId],
    queryFn: () => api.get<LeaveEntry[]>(`/orgs/${orgId}/members/${userId}/leave`),
    enabled: !!orgId && !!userId,
  });
}

function invalidateLeave(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'leave'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'shifts'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'ops'] }),
    qc.invalidateQueries({ queryKey: ['me', 'shifts'] }),
  ]);
}

export function useSetLeave(orgId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; startsOn: string; endsOn: string | null; reason: string | null }) =>
      id ? api.patch<LeaveSaved>(`/orgs/${orgId}/members/${userId}/leave/${id}`, body)
         : api.post<LeaveSaved>(`/orgs/${orgId}/members/${userId}/leave`, body),
    onSuccess: () => invalidateLeave(qc, orgId),
  });
}

/** Back from today (planned leave is removed). */
export function useEndLeave(orgId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ended: true }>(`/orgs/${orgId}/members/${userId}/leave/${id}/end`, {}),
    onSuccess: () => invalidateLeave(qc, orgId),
  });
}

export function useDeleteLeave(orgId: string, userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true }>(`/orgs/${orgId}/members/${userId}/leave/${id}`),
    onSuccess: () => invalidateLeave(qc, orgId),
  });
}

/** Admin sets or changes a member's email (their sign-in email if they use the app). */
export function useSetMemberEmail(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string | null }) =>
      api.patch<{ userId: string }>(`/orgs/${orgId}/members/${userId}`, { email }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'invites'] }),
    ]),
  });
}

/** New temporary password for a locked-out member; shown once to the admin. */
export function useResetMemberSignIn(orgId: string) {
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<{ email: string | null; tempPassword: string }>(`/orgs/${orgId}/members/${userId}/reset-sign-in`, {}),
  });
}

// ── Invites (email-bound claim links) ──────────────────────────

export function useOrgInvites(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'invites'],
    queryFn: () => api.get<Invite[]>(`/orgs/${orgId}/invites`),
    enabled: !!orgId,
  });
}

export function useCreateInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; displayName: string; role: UserRole; expiresInHours?: number; userId?: string }) =>
      api.post<Invite>(`/orgs/${orgId}/invites`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'invites'] }),
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
    ]),
  });
}

export function useRevokeInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.delete<void>(`/orgs/${orgId}/invites/${inviteId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'invites'] }),
  });
}

export function useInvitePreview(token: string) {
  return useQuery({
    queryKey: ['invites', token],
    queryFn: () => apiPublic.get<InvitePreview>(`/invites/${token}`),
    enabled: !!token,
    retry: false,
  });
}

export function useClaimInvite(token: string) {
  return useMutation({
    mutationFn: () => apiPublic.post<ClaimResult>(`/invites/${token}/claim`),
  });
}

/** Signed in already (another unit): join the invite's unit with this account. */
export function useAcceptInvite(token: string) {
  return useMutation({
    mutationFn: () => api.post<{ orgId: string }>(`/invites/${token}/accept`, {}),
  });
}

// ── Categories ───────────────────────────────────────────────

export function useCategories(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'categories'],
    queryFn: () => api.get<Category[]>(`/orgs/${orgId}/categories`),
    enabled: !!orgId,
  });
}

export function useCreateCategory(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      api.post<Category>(`/orgs/${orgId}/categories`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'categories'] }),
  });
}

export function useUpdateCategory(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string }) =>
      api.patch<Category>(`/orgs/${orgId}/categories/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'categories'] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'equipment'] }); // rows show the name
    },
  });
}

export function useRetireCategory(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/orgs/${orgId}/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'categories'] }),
  });
}

// ── Teams ─────────────────────────────────────────────────────

export function useTeams(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'teams'],
    queryFn: () => api.get<Team[]>(`/orgs/${orgId}/teams`),
    enabled: !!orgId,
  });
}

export function useTeam(orgId: string, teamId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'teams', teamId],
    queryFn: () => api.get<Team>(`/orgs/${orgId}/teams/${teamId}`),
    enabled: !!orgId && !!teamId,
  });
}

export function useTeamMembers(orgId: string, teamId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'teams', teamId, 'members'],
    queryFn: () => api.get<TeamMember[]>(`/orgs/${orgId}/teams/${teamId}/members`),
    enabled: !!orgId && !!teamId,
  });
}

export function useCreateTeam(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      api.post<Team>(`/orgs/${orgId}/teams`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'teams'] }),
  });
}

function invalidateTeamMembership(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'teams'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'members'] }),
    qc.invalidateQueries({ queryKey: ['me'] }),
  ]);
}

/** Admin: pause (disable) or resume a team. Disabled teams keep members but leave rotations. */
export function useSetTeamDisabled(orgId: string, teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (disabled: boolean) => api.patch<Team>(`/orgs/${orgId}/teams/${teamId}`, { disabled }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'teams'] }),
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'schedules'] }),
    ]),
  });
}

export function useAddTeamMember(orgId: string, teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { userId: string; role: TeamRole }) =>
      api.post<TeamMember>(`/orgs/${orgId}/teams/${teamId}/members`, body),
    // A move also changes the old team, and members/me show each person's team.
    onSuccess: () => invalidateTeamMembership(qc, orgId),
  });
}

export function useRemoveTeamMember(orgId: string, teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<void>(`/orgs/${orgId}/teams/${teamId}/members/${userId}`),
    // A move also changes the old team, and members/me show each person's team.
    onSuccess: () => invalidateTeamMembership(qc, orgId),
  });
}

// ── Equipment ─────────────────────────────────────────────────

export function useEquipment(orgId: string, params?: { categoryId?: string; type?: string; kind?: string }) {
  const qs = params
    ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString()
    : '';
  return useQuery({
    queryKey: ['orgs', orgId, 'equipment', params],
    queryFn: () => api.get<Equipment[]>(`/orgs/${orgId}/equipment${qs}`),
    enabled: !!orgId,
  });
}

export function useEquipmentItem(orgId: string, equipmentId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'equipment', equipmentId],
    queryFn: () => api.get<Equipment>(`/orgs/${orgId}/equipment/${equipmentId}`),
    enabled: !!orgId && !!equipmentId,
  });
}

export function useCreateEquipment(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Equipment>) =>
      api.post<Equipment>(`/orgs/${orgId}/equipment`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'equipment'] }),
  });
}

export function useUpdateEquipment(orgId: string, equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Equipment>) =>
      api.patch<Equipment>(`/orgs/${orgId}/equipment/${equipmentId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'equipment'] });
    },
  });
}

export function useRetireEquipment(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (equipmentId: string) => api.delete<void>(`/orgs/${orgId}/equipment/${equipmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'equipment'] }),
  });
}

// ── Placement & moving ────────────────────────────────────────

export interface Places {
  sections: { sectionId: string; assetId: string; assetName: string; assetType: AssetType; path: string }[];
  locations: { locationId: string; name: string }[];
}

export interface Placement {
  sectionId?: string; locationId?: string; quantity: number; label: string;
}

/** Every asset section and storage location — destinations for "Move to…". */
export function usePlaces(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['orgs', orgId, 'places'],
    queryFn: () => api.get<Places>(`/orgs/${orgId}/places`),
    enabled: !!orgId && enabled,
  });
}

export function useEquipmentPlacements(orgId: string, equipmentId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'equipment', equipmentId, 'placements'],
    queryFn: () => api.get<Placement[]>(`/orgs/${orgId}/equipment/${equipmentId}/placements`),
    enabled: !!orgId && !!equipmentId,
  });
}

type PlaceRef = { sectionId?: string; locationId?: string };

export function useMoveEquipment(orgId: string, equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: PlaceRef; to: PlaceRef; quantity?: number }) =>
      api.post<{ name: string; from: string; to: string; quantity: number }>(
        `/orgs/${orgId}/equipment/${equipmentId}/move`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'equipment', equipmentId, 'placements'] }),
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'locations'] }), // stock lists + counts
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets'] }),    // section layouts + counts
    ]),
  });
}

// ── Locations ─────────────────────────────────────────────────

export function useLocations(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'locations'],
    queryFn: () => api.get<Location[]>(`/orgs/${orgId}/locations`),
    enabled: !!orgId,
  });
}

export function useCreateLocation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      api.post<Location>(`/orgs/${orgId}/locations`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'locations'] }),
  });
}

export function useUpdateLocation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string | null }) =>
      api.patch<Location>(`/orgs/${orgId}/locations/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'locations'] }),
  });
}

export function useRetireLocation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/orgs/${orgId}/locations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'locations'] }),
  });
}

export function useLocationEquipment(orgId: string, locationId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'locations', locationId, 'equipment'],
    queryFn: () => api.get<LocationEquipment[]>(`/orgs/${orgId}/locations/${locationId}/equipment`),
    enabled: !!orgId && !!locationId,
  });
}

export function useSetLocationEquipment(orgId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { equipmentId: string; quantity?: number }) =>
      api.post<LocationEquipment>(`/orgs/${orgId}/locations/${locationId}/equipment`, body),
    // also refreshes the list's per-location item counts
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'locations'] }),
  });
}

export function useRemoveLocationEquipment(orgId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (equipmentId: string) =>
      api.delete<void>(`/orgs/${orgId}/locations/${locationId}/equipment/${equipmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'locations'] }),
  });
}

// ── Assets ────────────────────────────────────────────────────

export function useAssets(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'assets'],
    queryFn: () => api.get<Asset[]>(`/orgs/${orgId}/assets`),
    enabled: !!orgId,
  });
}

export function useAsset(orgId: string, assetId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'assets', assetId],
    queryFn: () => api.get<Asset>(`/orgs/${orgId}/assets/${assetId}`),
    enabled: !!orgId && !!assetId,
  });
}

export function useCreateAsset(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; assetType: AssetType; registration?: string; notes?: string }) =>
      api.post<Asset>(`/orgs/${orgId}/assets`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets'] }),
  });
}

export function useUpdateAsset(orgId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Asset>) =>
      api.patch<Asset>(`/orgs/${orgId}/assets/${assetId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets'] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId] });
    },
  });
}

export function useRetireAsset(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => api.delete<void>(`/orgs/${orgId}/assets/${assetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets'] }),
  });
}

/** Admin: save a new asset order (only the rows whose position changed). */
export function useReorderAssets(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      api.post<void>(`/orgs/${orgId}/assets/reorder`, { order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets'] }),
  });
}

export function useSections(orgId: string, assetId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'assets', assetId, 'sections'],
    queryFn: () => api.get<AssetSection[]>(`/orgs/${orgId}/assets/${assetId}/sections`),
    enabled: !!orgId && !!assetId,
  });
}

export function useCreateSection(orgId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; parentSectionId?: string }) =>
      api.post<AssetSection>(`/orgs/${orgId}/assets/${assetId}/sections`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId, 'sections'] });
    },
  });
}

/** Admin: re-number sibling sections (the walk-around order checks follow). */
export function useReorderSections(orgId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      api.post<void>(`/orgs/${orgId}/assets/${assetId}/sections/reorder`, { order }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId, 'sections'] }),
  });
}

/** Admin: re-number the equipment inside one section. */
export function useReorderSectionItems(orgId: string, assetId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: { id: string; sortOrder: number }[]) =>
      Promise.all(order.map(({ id, sortOrder }) =>
        api.patch<void>(`/orgs/${orgId}/assets/${assetId}/sections/${sectionId}/equipment/${id}`, { sortOrder }))),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId, 'sections'] }),
  });
}

export function useAddSectionEquipment(orgId: string, assetId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { equipmentId: string; quantity: number }) =>
      api.post<void>(`/orgs/${orgId}/assets/${assetId}/sections/${sectionId}/equipment`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId, 'sections'] });
    },
  });
}

export function useRemoveSectionEquipment(orgId: string, assetId: string, sectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<void>(
        `/orgs/${orgId}/assets/${assetId}/sections/${sectionId}/equipment/${assignmentId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets', assetId, 'sections'] });
    },
  });
}

// ── Schedules ─────────────────────────────────────────────────

export function useSchedules(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'schedules'],
    queryFn: () => api.get<Schedule[]>(`/orgs/${orgId}/schedules`),
    enabled: !!orgId,
  });
}

export function useSchedule(orgId: string, scheduleId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'schedules', scheduleId],
    queryFn: () => api.get<Schedule>(`/orgs/${orgId}/schedules/${scheduleId}`),
    enabled: !!orgId && !!scheduleId,
  });
}

export function useCreateSchedule(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      cadence: 'weekly' | 'monthly' | 'quarterly';
      startDate: string;
      rotation: Rotation;
    }) => api.post<Schedule>(`/orgs/${orgId}/schedules`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'schedules'] }),
  });
}

/** Week-by-week plan (roster weeks + rotation prediction) from this week. */
export function useRoster(orgId: string, scheduleId: string, weeks: number) {
  return useQuery({
    queryKey: ['orgs', orgId, 'schedules', scheduleId, 'roster', weeks],
    queryFn: () => api.get<RosterWeek[]>(`/orgs/${orgId}/schedules/${scheduleId}/roster?weeks=${weeks}`),
    enabled: !!orgId && !!scheduleId,
  });
}

export function useSetRosterWeek(orgId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ periodStart, ...body }: {
      periodStart: string; note: string | null;
      assignments: { assetId: string; teamId: string | null }[];
    }) => api.put<void>(`/orgs/${orgId}/schedules/${scheduleId}/roster/${periodStart}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'schedules'] }),
  });
}

export function useClearRosterWeek(orgId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (periodStart: string) =>
      api.delete<void>(`/orgs/${orgId}/schedules/${scheduleId}/roster/${periodStart}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'schedules'] }),
  });
}

// ── Duty roster, holidays, shifts ────────────────────────────

/** Anything that changes who's on: refresh the roster, shifts and dashboard crews. */
function invalidateRoster(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'duty-roster'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'shifts'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'schedules'] }),
    qc.invalidateQueries({ queryKey: ['me', 'shifts'] }),
  ]);
}

export function useDutyRoster(orgId: string, weeks: number, from?: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'duty-roster', from ?? 'now', weeks],
    queryFn: () => api.get<DutyRoster>(`/orgs/${orgId}/duty-roster?weeks=${weeks}${from ? `&from=${from}` : ''}`),
    enabled: !!orgId,
  });
}

export function useSetDutyWeek(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ periodStart, ...body }: {
      periodStart: string; dutyTeamId?: string | null; opsTeamId?: string | null;
      udoUserId?: string | null; dayUdoUserId?: string | null; note?: string | null;
    }) => api.put(`/orgs/${orgId}/duty-roster/${periodStart}`, body),
    onSuccess: () => invalidateRoster(qc, orgId),
  });
}

export function useClearDutyWeek(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (periodStart: string) => api.delete(`/orgs/${orgId}/duty-roster/${periodStart}`),
    onSuccess: () => invalidateRoster(qc, orgId),
  });
}

export function useFillDutyRoster(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { from: string; weeks: number }) =>
      api.post<{ filled: string[] }>(`/orgs/${orgId}/duty-roster/fill`, body),
    onSuccess: () => invalidateRoster(qc, orgId),
  });
}

export function useSetDefaultDayUdo(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (defaultDayUdoId: string | null) => api.put(`/orgs/${orgId}/duty-roster-settings`, { defaultDayUdoId }),
    onSuccess: () => invalidateRoster(qc, orgId),
  });
}

export function useAddHoliday(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (h: Holiday) => api.post<Holiday>(`/orgs/${orgId}/holidays`, h),
    onSuccess: () => invalidateRoster(qc, orgId),
  });
}

export function useRemoveHoliday(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => api.delete(`/orgs/${orgId}/holidays/${date}`),
    onSuccess: () => invalidateRoster(qc, orgId),
  });
}

/** All shifts between two local dates (each read as 06:00 that day). */
export function useShifts(orgId: string, from: string, to: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'shifts', from, to],
    queryFn: () => api.get<Shift[]>(`/orgs/${orgId}/shifts?from=${from}&to=${to}`),
    enabled: !!orgId && !!from,
  });
}

/** My upcoming shifts — or, for a leader/admin, a member's (`forUserId`). */
export function useMyShifts(forUserId?: string | null) {
  return useQuery({
    queryKey: ['me', 'shifts', forUserId ?? 'me'],
    queryFn: () => api.get<MyShift[]>(forUserId ? `/me/shifts?for=${encodeURIComponent(forUserId)}` : '/me/shifts'),
  });
}

// ── Cover requests ───────────────────────────────────────────

function invalidateCover(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'cover'] }),
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'shifts'] }),
    qc.invalidateQueries({ queryKey: ['me', 'shifts'] }),
  ]);
}

/** Impact + who to ask, before sending a request. */
export function useCoverOptions(orgId: string, kind: CoverKind | null, startsAt: string | null, endsAt: string | null, forUserId?: string | null) {
  return useQuery({
    queryKey: ['orgs', orgId, 'cover', 'options', kind, startsAt, endsAt, forUserId ?? null],
    queryFn: () => api.get<CoverOptions>(
      `/orgs/${orgId}/cover-options?kind=${kind}&startsAt=${encodeURIComponent(startsAt!)}&endsAt=${encodeURIComponent(endsAt!)}`
      + (forUserId ? `&for=${encodeURIComponent(forUserId)}` : '')),
    enabled: !!orgId && !!kind && !!startsAt && !!endsAt,
    // Ticking another shift or changing the hours keeps the last answer on screen
    // while the new one loads, instead of the cards vanishing and reappearing.
    placeholderData: keepPreviousData,
  });
}

export function useCreateCover(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: CoverKind; startsAt: string; endsAt: string; note: string | null; forUserId?: string }) =>
      api.post<CoverRequest>(`/orgs/${orgId}/cover-requests`, body),
    onSuccess: () => invalidateCover(qc, orgId),
  });
}

export function useCoverRequests(orgId: string, scope: 'open' | 'mine') {
  return useQuery({
    queryKey: ['orgs', orgId, 'cover', 'list', scope],
    queryFn: () => api.get<CoverRequest[]>(`/orgs/${orgId}/cover-requests?scope=${scope}`),
    enabled: !!orgId,
  });
}

export function useCoverRequest(orgId: string, id: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'cover', 'one', id],
    queryFn: () => api.get<CoverRequest>(`/orgs/${orgId}/cover-requests/${id}`),
    enabled: !!orgId && !!id,
  });
}

export function useTakeCover(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    /** several ranges in one go (a request can have separate windows) — one notification */
    mutationFn: (ranges: { startsAt: string; endsAt: string }[]) =>
      api.post<CoverRequest>(`/orgs/${orgId}/cover-requests/${id}/take`, { ranges }),
    onSettled: () => invalidateCover(qc, orgId),
  });
}

export function useWithdrawTake(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (takeId: string) => api.post<CoverRequest>(`/orgs/${orgId}/cover-requests/${id}/takes/${takeId}/withdraw`, {}),
    onSuccess: () => invalidateCover(qc, orgId),
  });
}

export function useCancelCover(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CoverRequest>(`/orgs/${orgId}/cover-requests/${id}/cancel`, {}),
    onSuccess: () => invalidateCover(qc, orgId),
  });
}

export function useMyCoverStats() {
  return useQuery({ queryKey: ['me', 'cover-stats'], queryFn: () => api.get<MyCoverStats>('/me/cover-stats') });
}

export function useNudge(orgId: string, id: string) {
  return useMutation({
    mutationFn: (userId: string) => api.post<{ nudged: boolean }>(`/orgs/${orgId}/cover-requests/${id}/nudge`, { userId }),
  });
}

// ── Swaps ────────────────────────────────────────────────────

function invalidateSwaps(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'swaps'] }),
    invalidateCover(qc, orgId),
  ]);
}

export function useSwapOptions(orgId: string, give: SwapSide | null) {
  return useQuery({
    queryKey: ['orgs', orgId, 'swaps', 'options', give],
    queryFn: () => api.get<SwapOptions>(
      `/orgs/${orgId}/swap-options?kind=${give!.kind}&startsAt=${encodeURIComponent(give!.startsAt)}&endsAt=${encodeURIComponent(give!.endsAt)}`),
    enabled: !!orgId && !!give,
  });
}

export function useCreateSwap(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { targetId: string; give: SwapSide; want: SwapSide | null; note: string | null }) =>
      api.post<SwapOffer>(`/orgs/${orgId}/swaps`, body),
    onSuccess: () => invalidateSwaps(qc, orgId),
  });
}

export function useSwaps(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'swaps', 'list'],
    queryFn: () => api.get<SwapOffer[]>(`/orgs/${orgId}/swaps`),
    enabled: !!orgId,
  });
}

export function useSwap(orgId: string, id: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'swaps', 'one', id],
    queryFn: () => api.get<SwapOffer>(`/orgs/${orgId}/swaps/${id}`),
    enabled: !!orgId && !!id,
  });
}

export function useSwapAction(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, want }: { action: 'accept' | 'decline' | 'cancel'; want?: SwapSide }) =>
      api.post<SwapOffer>(`/orgs/${orgId}/swaps/${id}/${action}`, want ? { want } : {}),
    onSuccess: () => invalidateSwaps(qc, orgId),
  });
}

// ── Operational support ──────────────────────────────────────

/** A week's ops-support shifts; no week = my team's next ops week. */
export function useOpsWeek(orgId: string, week: string | null) {
  return useQuery({
    queryKey: ['orgs', orgId, 'ops', 'week', week ?? 'next'],
    queryFn: () => api.get<OpsWeek>(`/orgs/${orgId}/ops-week${week ? `?week=${week}` : ''}`),
    enabled: !!orgId,
  });
}

export function useOpsAction(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shiftId, action, userId }: { shiftId: string; action: 'claim' | 'release' | 'assign'; userId?: string | null }) =>
      api.post<OpsWeek>(`/orgs/${orgId}/ops-shifts/${shiftId}/${action}`, action === 'assign' ? { userId: userId ?? null } : {}),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'ops'] }),
      invalidateCover(qc, orgId),
    ]),
  });
}

export function useOpsPreferences() {
  return useQuery({ queryKey: ['me', 'ops-preferences'], queryFn: () => api.get<OpsPreference[]>('/me/ops-preferences') });
}

export function useSaveOpsPreferences(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (preferences: OpsPreference[]) => api.put<OpsPreference[]>('/me/ops-preferences', { preferences }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: ['me', 'ops-preferences'] }),
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'ops'] }),
      invalidateCover(qc, orgId),
    ]),
  });
}

export function useOpsStats(orgId: string, teamId: string | null) {
  return useQuery({
    queryKey: ['orgs', orgId, 'ops', 'stats', teamId],
    queryFn: () => api.get<OpsMember[]>(`/orgs/${orgId}/ops-stats?teamId=${teamId}`),
    enabled: !!orgId && !!teamId,
  });
}

// ── Activities ───────────────────────────────────────────────

function invalidateActivities(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'activities'] }),
    qc.invalidateQueries({ queryKey: ['me', 'shifts'] }),
  ]);
}

export function useActivities(orgId: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'activities', 'list', from ?? 'now', to ?? '8w'],
    queryFn: () => api.get<Activity[]>(`/orgs/${orgId}/activities${from ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to!)}` : ''}`),
    enabled: !!orgId,
  });
}

export function useActivity(orgId: string, id: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'activities', 'one', id],
    queryFn: () => api.get<ActivityDetail>(`/orgs/${orgId}/activities/${id}`),
    enabled: !!orgId && !!id,
  });
}

export function useRespondActivity(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (response: ActivityResponse | null) => api.put<ActivityDetail>(`/orgs/${orgId}/activities/${id}/response`, { response }),
    onSuccess: () => invalidateActivities(qc, orgId),
  });
}

export type ActivityInput = {
  kind?: ActivityKind; title?: string; startsAt?: string; endsAt?: string; location?: string | null;
  description?: string | null; hostTeamId?: string | null; peopleNeeded?: number | null; wantedQuals?: string[] | null;
  notify?: boolean;
};

export function useSaveActivity(orgId: string, id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActivityInput) => id
      ? api.patch<ActivityDetail>(`/orgs/${orgId}/activities/${id}`, body)
      : api.post<ActivityDetail>(`/orgs/${orgId}/activities`, body),
    onSuccess: () => invalidateActivities(qc, orgId),
  });
}

export function useCancelActivity(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ActivityDetail>(`/orgs/${orgId}/activities/${id}/cancel`, {}),
    onSuccess: () => invalidateActivities(qc, orgId),
  });
}

export function useTrainingNight(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'activities', 'training-night'],
    queryFn: () => api.get<TrainingNight>(`/orgs/${orgId}/training-night`),
    enabled: !!orgId,
  });
}

export function useSaveTrainingNight(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TrainingNight) => api.put<TrainingNight>(`/orgs/${orgId}/training-night`, body),
    onSuccess: () => invalidateActivities(qc, orgId),
  });
}

// ── Scratch weeks ────────────────────────────────────────────

export function useScratchWindows(orgId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'scratch', 'list'],
    queryFn: () => api.get<ScratchWindow[]>(`/orgs/${orgId}/scratch-windows`),
    enabled: !!orgId,
  });
}

export function useScratchWindow(orgId: string, id: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'scratch', 'one', id],
    queryFn: () => api.get<ScratchWindowDetail>(`/orgs/${orgId}/scratch-windows/${id}`),
    enabled: !!orgId && !!id,
  });
}

function invalidateScratch(qc: ReturnType<typeof useQueryClient>, orgId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['orgs', orgId, 'scratch'] }),
    invalidateCover(qc, orgId),
  ]);
}

export function useCreateScratchWindow(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; fromWeek: string; toWeek: string; replyBy: string }) =>
      api.post<ScratchWindowDetail>(`/orgs/${orgId}/scratch-windows`, body),
    onSuccess: () => invalidateScratch(qc, orgId),
  });
}

export function useSaveAvailability(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shiftIds: string[]) => api.put<ScratchWindowDetail>(`/orgs/${orgId}/scratch-windows/${id}/availability`, { shiftIds }),
    onSuccess: () => invalidateScratch(qc, orgId),
  });
}

export function useSaveScratchDraft(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slots: { shiftId: string; udoUserId: string | null; crew: string[] }[]) =>
      api.put<ScratchWindowDetail>(`/orgs/${orgId}/scratch-windows/${id}/draft`, { slots }),
    onSuccess: (data) => qc.setQueryData(['orgs', orgId, 'scratch', 'one', id], data),
  });
}

export function useScratchAction(orgId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'suggest' | 'publish' | 'remind') => api.post<ScratchWindowDetail | { reminded: number }>(`/orgs/${orgId}/scratch-windows/${id}/${action}`, {}),
    onSuccess: () => invalidateScratch(qc, orgId),
  });
}

/** Slots (asset ids per slot, in order) and the teams rotating through them. */
export interface Rotation { slots: string[][]; teamIds: string[] }

export function useUpdateRotation(orgId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rotation: Rotation) =>
      api.put<{ updated: boolean }>(`/orgs/${orgId}/schedules/${scheduleId}/rotation`, rotation),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'schedules'] }),
  });
}

export function useGenerateRuns(orgId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ created: unknown[]; skipped: string[]; uncoveredSlots: number[] }>(
        `/orgs/${orgId}/schedules/${scheduleId}/generate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs'] });
    },
  });
}

// ── Check runs ────────────────────────────────────────────────

// ── Dashboard & history ───────────────────────────────────────

export interface DashboardData {
  today: string;
  alertWindowDays: number;
  overdueRuns: {
    id: string; periodStart: string; periodEnd: string;
    assetName: string; assetType: AssetType; teamName: string;
    itemCount: number; checkedCount: number;
  }[];
  expiring: DueEquipment[];
  serviceDue: DueEquipment[];
  openFaults: Fault[];
  offRun: OffRunAsset[];
}

export interface DueEquipment {
  id: string; name: string; serialNumber: string | null; equipmentType: string;
  dueDate: string; categoryName: string | null; location: string | null;
}

/** Admin overview: overdue checks, open issues, expiry / service alerts. */
export function useDashboard(orgId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['orgs', orgId, 'dashboard'],
    queryFn: () => api.get<DashboardData>(`/orgs/${orgId}/dashboard`),
    enabled: !!orgId && enabled,
  });
}

export interface RunHistoryEntry {
  id: string; action: string; entityType: string; entityId: string;
  oldValue: Record<string, unknown> | null; newValue: Record<string, unknown> | null;
  createdAt: string; actorName: string | null; actorActive: boolean | null;
  equipmentName: string | null;
}

/** Everything recorded on one inspection (the run and its items). */
export function useRunHistory(orgId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['orgs', orgId, 'check-runs', runId, 'history'],
    queryFn: () => api.get<RunHistoryEntry[]>(`/orgs/${orgId}/check-runs/${runId}/history`),
    enabled: !!orgId && !!runId && enabled,
  });
}

export interface EquipmentHistoryEntry {
  itemId: string; status: CheckStatus; note: string | null; flagged: boolean;
  externalRef: string | null; checkedAt: string | null;
  runId: string; periodStart: string; runStatus: RunStatus; submittedAt: string | null;
  assetName: string; assetType: AssetType; sectionName: string | null;
  checkedByName: string | null; checkedByActive: boolean | null; photoCount: number;
}

/** One piece of equipment's results across past inspections, newest first. */
export function useEquipmentHistory(orgId: string, equipmentId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'equipment', equipmentId, 'history'],
    queryFn: () => api.get<EquipmentHistoryEntry[]>(`/orgs/${orgId}/equipment/${equipmentId}/history`),
    enabled: !!orgId && !!equipmentId,
  });
}

export function useCheckRuns(orgId: string, params?: { status?: RunStatus; teamId?: string }) {
  const qs = params
    ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString()
    : '';
  return useQuery({
    queryKey: ['orgs', orgId, 'check-runs', params],
    queryFn: () => api.get<CheckRunSummary[]>(`/orgs/${orgId}/check-runs${qs}`),
    enabled: !!orgId,
  });
}

/** A check, with this phone's changes that are still waiting to sync applied. */
export function useCheckRun(orgId: string, runId: string) {
  useOutbox(); // re-render as waiting changes come and go
  const q = useQuery({
    queryKey: ['orgs', orgId, 'check-runs', runId],
    queryFn: () => api.get<CheckRunDetail>(`/orgs/${orgId}/check-runs/${runId}`),
    enabled: !!orgId && !!runId,
    // Several people check one truck at once: keep everyone's ticks in view.
    refetchInterval: (q) => (q.state.data?.status === 'draft' ? 10_000 : false),
  });
  return { ...q, data: q.data && overlayRun(q.data) };
}

/** Start an on-demand check (or get back an open one for the same asset). */
export function useCreateRun(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { assetId: string; teamId?: string }) =>
      api.post<CheckRun & { resumed?: boolean }>(`/orgs/${orgId}/check-runs`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs'] }),
  });
}

/** Throw away an on-demand check started by mistake (nothing recorded yet). */
export function useDiscardRun(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.delete<void>(`/orgs/${orgId}/check-runs/${runId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs'] }),
  });
}

export interface RunPhoto { id: string; itemId: string; caption: string | null; url: string }

/** All photos on a check with view URLs (for the printable report). */
export function useRunPhotos(orgId: string, runId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'check-runs', runId, 'photos'],
    queryFn: () => api.get<RunPhoto[]>(`/orgs/${orgId}/check-runs/${runId}/photos`),
    enabled: !!orgId && !!runId,
    staleTime: 5 * 60_000, // photo links last 15 minutes
  });
}

export function useSubmitRun(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, submissionNote }: { runId: string; submissionNote: string }) =>
      api.post<CheckRun>(`/orgs/${orgId}/check-runs/${runId}/submit`, { submissionNote }),
    onSuccess: (_data, { runId }) => {
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs'] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs', runId] });
    },
  });
}

// ── Check items ───────────────────────────────────────────────

/** placeholder: the row from the run's list, so a wizard step renders before the fetch lands. */
export function useCheckItem(itemId: string, placeholder?: CheckItemDetail) {
  useOutbox();
  const q = useQuery({
    queryKey: ['check-items', itemId],
    queryFn: () => api.get<CheckItemDetail>(`/check-items/${itemId}`),
    enabled: !!itemId,
    placeholderData: placeholder,
  });
  return { ...q, data: q.data && overlayItem(q.data) };
}

/**
 * Save a change to a check item. It goes through the offline outbox: shown
 * straight away, kept on the phone, and sent as soon as there's signal.
 */
export function useSaveCheckItem(orgId: string, runId: string) {
  const qc = useQueryClient();
  return {
    isPending: false,
    mutate: ({ itemId, ...fields }: {
      itemId: string; status?: CheckStatus; note?: string; externalRef?: string; flagged?: boolean; reading?: string;
    }) => {
      const run = qc.getQueryData<CheckRunDetail>(['orgs', orgId, 'check-runs', runId]);
      const inRun = run?.items.find((i) => i.id === itemId);
      const detail = qc.getQueryData<CheckItemDetail>(['check-items', itemId]);
      enqueueItem({
        orgId, runId, itemId, fields,
        label: inRun?.equipmentName ?? detail?.equipmentName ?? 'Item',
        base: baseOf(detail ?? inRun),
      });
      patchRunItems(qc, orgId, runId, [itemId], fields);
    },
  };
}

/** "All OK" for a section: one status for many unchecked items (readings excluded server-side). */
export function useMarkItems(orgId: string, runId: string) {
  const qc = useQueryClient();
  return {
    isPending: false,
    mutate: ({ itemIds, status }: { itemIds: string[]; status: CheckStatus }) => {
      enqueueMark({ orgId, runId, itemIds, status });
      patchRunItems(qc, orgId, runId, itemIds, { status });
    },
  };
}

function patchRunItems(
  qc: ReturnType<typeof useQueryClient>, orgId: string, runId: string,
  itemIds: string[], fields: Partial<CheckItemInRun>,
) {
  const ids = new Set(itemIds);
  // Don't let a background refresh that started before this tap overwrite it.
  void qc.cancelQueries({ queryKey: ['orgs', orgId, 'check-runs', runId] });
  const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));
  qc.setQueryData<CheckRunDetail>(['orgs', orgId, 'check-runs', runId], (run) => run && {
    ...run,
    items: run.items.map((it) => (ids.has(it.id) ? { ...it, ...clean } : it)),
  });
}

/** Mark an issue from a submitted check fixed; clears it from open issues. */
export function useResolveIssue(orgId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.post<void>(`/check-items/${itemId}/resolve`, { note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['check-items', itemId] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'dashboard'] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'faults'] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs'] });
    },
  });
}

/** Admin: undo a fix marked by mistake. */
export function useReopenIssue(orgId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(`/check-items/${itemId}/resolve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['check-items', itemId] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'dashboard'] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'faults'] });
    },
  });
}

// ── Photos ────────────────────────────────────────────────────

/** Add a photo to an item. Kept on the phone and uploaded when there's signal. */
export function useUploadPhoto(orgId: string, runId: string) {
  const qc = useQueryClient();
  return {
    isPending: false,
    /** `original`: the photo as taken, when `file` is its marked-up copy. */
    mutate: ({ itemId, file, original }: { itemId: string; file: File; original?: File }) => {
      const contentType = photoContentType(file);
      const originalContentType = original ? photoContentType(original) : null;
      if (!contentType || (original && !originalContentType)) { emitError('That file type isn’t supported — use a JPEG, PNG, WebP or HEIC photo'); return; }
      const run = qc.getQueryData<CheckRunDetail>(['orgs', orgId, 'check-runs', runId]);
      enqueuePhoto({
        orgId, runId, itemId, blob: file, name: file.name || 'photo.jpg', contentType,
        label: run?.items.find((i) => i.id === itemId)?.equipmentName ?? 'Photo',
        ...(original && originalContentType ? { original, originalContentType } : {}),
      });
    },
  };
}

const PHOTO_TYPES: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif',
};

/** The photo's type as the API accepts it. Some phones give no type, so fall back to the extension. */
function photoContentType(file: File): string | null {
  if (Object.values(PHOTO_TYPES).includes(file.type)) return file.type;
  if (file.type) return null;
  return PHOTO_TYPES[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? null;
}

/** Remove an uploaded photo (needs signal). One not sent yet: removePendingPhoto. */
export function useDeletePhoto(orgId: string, runId: string, itemId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) =>
      api.delete<void>(`/photos/${photoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['check-items', itemId] });
      qc.invalidateQueries({ queryKey: ['orgs', orgId, 'check-runs', runId] });
    },
  });
}

// ── Search ────────────────────────────────────────────────────

export function useSearch(
  orgId: string,
  params: { q?: string; categoryId?: string; type?: string; assetId?: string; expirySoon?: boolean },
) {
  const qs = '?' + new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '' && v !== false)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return useQuery({
    queryKey: ['orgs', orgId, 'search', params],
    queryFn: () => api.get<SearchResult[]>(`/orgs/${orgId}/search${qs}`),
    enabled: !!orgId && (!!params.q || !!params.categoryId || !!params.assetId || !!params.expirySoon),
  });
}

// ── System admin (across units) ──────────────────────────────

export interface SystemMe { id: string; email: string; displayName: string; mfaSetupRequired: boolean }

/** Whether the signed-in person is a system admin; errors (403) when not. */
export function useSystemMe(enabled = true) {
  return useQuery({
    queryKey: ['system', 'me'],
    queryFn: () => api.get<SystemMe>('/system/me'),
    enabled,
    retry: false,
  });
}

export interface SystemOrgStats {
  id: string; name: string; slug: string; createdAt: string;
  members: number; appUsers: number; admins: number;
  active7d: number; active30d: number; lastSeenAt: string | null;
  teams: number; assets: number; checks30d: number;
  coverOpen: number; cover30d: number; shifts28d: number;
  pendingInvites: number; pushUsers: number; lastChangeAt: string | null;
}

export interface SystemOverview {
  totals: {
    orgs: number; people: number; appUsers: number; active7d: number; active30d: number;
    checks30d: number; coverOpen: number; cover30d: number;
  };
  orgs: SystemOrgStats[];
}

export function useSystemOverview() {
  return useQuery({
    queryKey: ['system', 'overview'],
    queryFn: () => api.get<SystemOverview>('/system/overview'),
  });
}

export interface SystemInvite { id: string; email: string; displayName: string; url: string; expiresAt: string }

export interface SystemOrgDetail {
  org: SystemOrgStats;
  admins: { userId: string; displayName: string; email: string | null; hasApp: boolean; lastSeenAt: string | null }[];
  adminInvites: { id: string; email: string; displayName: string; expiresAt: string; createdAt: string }[];
}

export function useSystemOrg(orgId: string) {
  return useQuery({
    queryKey: ['system', 'orgs', orgId],
    queryFn: () => api.get<SystemOrgDetail>(`/system/orgs/${orgId}`),
    enabled: !!orgId,
  });
}

export function useCreateSystemOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; slug: string; adminEmail: string; adminName: string }) =>
      api.post<{ org: { id: string; name: string }; invite: SystemInvite }>('/system/orgs', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system'] }),
  });
}

export function useUpdateSystemOrg(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; slug?: string }) => api.patch(`/system/orgs/${orgId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system'] }),
  });
}

export function useAddOrgAdmin(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; displayName: string }) =>
      api.post<{ promoted: boolean; invite: SystemInvite | null }>(`/system/orgs/${orgId}/admins`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system'] }),
  });
}

// ── Fault register & off run ──────────────────────────────────

const invalidateFaults = (qc: ReturnType<typeof useQueryClient>, orgId: string) => {
  qc.invalidateQueries({ queryKey: ['orgs', orgId, 'faults'] });
  qc.invalidateQueries({ queryKey: ['orgs', orgId, 'dashboard'] });
  qc.invalidateQueries({ queryKey: ['orgs', orgId, 'assets'] });
  qc.invalidateQueries({ queryKey: ['check-items'] });
};

/** Open faults (or those fixed lately) and the assets that are off run. */
export function useFaults(orgId: string, opts: { fixed?: boolean; assetId?: string } = {}) {
  const qs = new URLSearchParams();
  if (opts.fixed) qs.set('fixed', '1');
  if (opts.assetId) qs.set('assetId', opts.assetId);
  return useQuery({
    queryKey: ['orgs', orgId, 'faults', 'list', opts.fixed ? 'fixed' : 'open', opts.assetId ?? ''],
    queryFn: () => api.get<FaultList>(`/orgs/${orgId}/faults${qs.size ? `?${qs}` : ''}`),
    enabled: !!orgId,
  });
}

export function useFault(orgId: string, faultId: string) {
  return useQuery({
    queryKey: ['orgs', orgId, 'faults', faultId],
    queryFn: () => api.get<FaultDetail>(`/orgs/${orgId}/faults/${faultId}`),
    enabled: !!orgId && !!faultId,
  });
}

export function useReportFault(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      assetId: string; equipmentId?: string | null; title?: string | null; status: FaultStatus;
      note: string; externalRef?: string | null; suggestOffRun?: boolean;
    }) => api.post<Fault>(`/orgs/${orgId}/faults`, body),
    onSuccess: () => invalidateFaults(qc, orgId),
  });
}

export function useUpdateFault(orgId: string, faultId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { assignedTo?: string | null; externalRef?: string | null; status?: FaultStatus }) =>
      api.patch<Fault>(`/orgs/${orgId}/faults/${faultId}`, body),
    onSuccess: () => invalidateFaults(qc, orgId),
  });
}

export function useAddFaultNote(orgId: string, faultId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.post<void>(`/orgs/${orgId}/faults/${faultId}/notes`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs', orgId, 'faults', faultId] }),
  });
}

export function useResolveFault(orgId: string, faultId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => api.post<Fault & { stillOffRun: boolean }>(`/orgs/${orgId}/faults/${faultId}/resolve`, { note }),
    onSuccess: () => invalidateFaults(qc, orgId),
  });
}

export function useReopenFault(orgId: string, faultId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<Fault>(`/orgs/${orgId}/faults/${faultId}/resolve`),
    onSuccess: () => invalidateFaults(qc, orgId),
  });
}

/** Admin: take an asset off run (or change why / until when), or put it back on. */
export function useSetOffRun(orgId: string, assetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string; until?: string | null; faultId?: string | null } | null) => body
      ? api.put<{ offRun: OffRunAsset | null }>(`/orgs/${orgId}/assets/${assetId}/off-run`, body)
      : api.delete<{ offRun: null }>(`/orgs/${orgId}/assets/${assetId}/off-run`),
    onSuccess: () => invalidateFaults(qc, orgId),
  });
}
